/**
 * Purpose: A reusable animated gradient line component with a ping animation
 *
 * How it works:
 * 1. Renders a thin line with gradient background
 * 2. Applies an animate-ping effect for visual feedback
 * 3. Allows customization of position, z-index, and gradient colors
 *
 * Key features:
 * - Configurable position (top, bottom, or custom)
 * - Customizable z-index for layering
 * - Optional container height
 * - Customizable gradient colors
 */

import { cn } from "@/lib/utils";

export interface AnimatedGradientLineProps {
  /** Position preset or custom positioning classes */
  position?: "top" | "bottom" | string;
  /** Z-index value for the container */
  zIndex?: number;
  /** Height of the container (default is h-leftColumnHeaderHeight) */
  containerHeight?: string;
  /** Custom className for the container */
  className?: string;
  /** Custom className for the gradient line */
  lineClassName?: string;
}

export default function AnimatedGradientLine({
  position = "bottom",
  zIndex = 50,
  containerHeight = "h-leftColumnHeaderHeight",
  className,
  lineClassName,
}: AnimatedGradientLineProps) {
  // Determine position classes based on the position prop
  const positionClasses =
    position === "top"
      ? "inset-x-0 top-0"
      : position === "bottom"
        ? "inset-x-0 bottom-0"
        : position; // Use custom position if not "top" or "bottom"

  return (
    <div
      className={cn(
        "absolute w-full",
        positionClasses,
        containerHeight,
        className,
      )}
    >
      <div
        className={cn(
          "absolute inset-x-0 bottom-0 h-px w-full animate-ping bg-gradient-to-r from-background/30 via-foreground/50 to-background/30",
          lineClassName,
        )}
        style={{ zIndex }}
      />
    </div>
  );
}
